import React, { useState, useEffect } from 'react';

export type CollaboratorRole = 'owner' | 'collaborator' | 'viewer';
import { X, Plus, Loader } from 'lucide-react';
import { getAvailableCollaborators, addProjectCollaborator } from '../../api';
import './AddCollaboratorModal.css';

interface AvailableUser {
  id: number;
  name: string;
  email: string;
}

interface AddCollaboratorModalProps {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  onAdd: (userId: number, role: CollaboratorRole) => void;
  isAdding?: boolean;
}

const AddCollaboratorModal: React.FC<AddCollaboratorModalProps> = ({
  projectId,
  isOpen,
  onClose,
  onAdd,
  isAdding = false,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [role, setRole] = useState<CollaboratorRole>('collaborator');
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAvailableCollaborators(projectId);
        setAvailableUsers(response.data || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load users');
        setAvailableUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isOpen, projectId]);

  const filteredUsers = availableUsers.filter(
    user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = async () => {
    if (!selectedUserId) return;

    try {
      await onAdd(selectedUserId, role);
      setSelectedUserId(null);
      setSearchTerm('');
      // Modal will be closed by parent component
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add collaborator');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-96 max-h-96 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add Collaborator</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 scrollbar-hide">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-5 h-5 text-blue-600 animate-spin" />
            </div>
          ) : availableUsers.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">No users available to add</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Search */}
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* User List */}
              <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-hide">
                {filteredUsers.map(user => (
                  <label
                    key={user.id}
                    className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                  >
                    <input
                      type="radio"
                      name="collaborator"
                      value={user.id}
                      checked={selectedUserId === user.id}
                      onChange={() => setSelectedUserId(user.id)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 space-y-3">
          <label className="block text-sm text-gray-700">
            <span className="block mb-1 font-medium">Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as CollaboratorRole)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="collaborator">Collaborator — full access</option>
              <option value="viewer">Viewer — read only</option>
              <option value="owner">Owner — can manage members</option>
            </select>
          </label>
          <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!selectedUserId || isAdding || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isAdding ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add Collaborator
              </>
            )}
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCollaboratorModal;
